import { z } from 'zod';
import { defineEndpoint } from '../contract.ts';
import { timestamp } from '../schemas.ts';

// Each module's lifecycle (cutover milestone A, ADR 0020): a trial until an admin puts it
// live, one way. A trial module's data can be reset, completely and audited, so its import
// can run again fresh at cutover; a live module's never can. Both changes need
// core.system.manage, a recent login and the module's name typed to confirm.

/** `trial` until an admin says otherwise; `resetting` while a reset is under way; `live` for good. */
export const ModuleState = z.enum(['trial', 'resetting', 'live']);
export type ModuleState = z.infer<typeof ModuleState>;

const ModuleName = z.string().regex(/^[a-z][a-z0-9]*$/);
const ModuleParams = z.object({ name: ModuleName });

/** What an admin types to confirm: the module's name. */
export const ConfirmModule = z.object({ confirm: z.string().max(64) });

export const ModuleCollectionCount = z.object({
  name: z.string(),
  /** Null when the count couldn't be read, never 0. */
  rows: z.number().int().nullable(),
});
export type ModuleCollectionCount = z.infer<typeof ModuleCollectionCount>;

export const ModuleStatus = z.object({
  name: z.string(),
  title: z.string(),
  /** False when ITC_MODULES_DISABLED names it here. */
  enabled: z.boolean(),
  state: ModuleState,
  /** When it took its state, or null for a module that has always been a trial. */
  since: timestamp.nullable(),
  /** Who put it in its state: a core_users id and their name, or null. */
  by: z.object({ id: z.string(), name: z.string() }).nullable(),
  /** The module's own `<name>_*` collections, with their row counts, by name. */
  collections: z.array(ModuleCollectionCount),
  /** Its stored files; null when the count couldn't be read. */
  files: z.number().int().nullable(),
});
export type ModuleStatus = z.infer<typeof ModuleStatus>;

/** Every module's lifecycle, with what it holds. (`/admin/modules` is the shell's list of modules, for everyone.) */
export const listModuleStates = defineEndpoint({
  method: 'GET',
  path: '/admin/system/modules',
  permission: 'core.system.view',
  response: z.object({ items: z.array(ModuleStatus) }),
});

/** What one reset request did, and what is left. */
export const ResetProgress = z.object({
  module: z.string(),
  state: ModuleState,
  /** Every row and file is gone and the module is a trial again. False: ask again to continue. */
  done: z.boolean(),
  /** Rows left in each collection, as last counted. */
  remaining: z.record(z.string(), z.number().int()),
  /** Stored files left. */
  files: z.number().int(),
  /** The module's pending scheduled events left to cancel: they name rows the reset deleted. */
  scheduled: z.number().int(),
  /** Rows deleted by this reset so far, across every request, by collection. */
  deleted: z.record(z.string(), z.number().int()),
  /** Files deleted by this reset so far. */
  deletedFiles: z.number().int(),
  /** Scheduled events cancelled by this reset so far. */
  cancelledScheduled: z.number().int(),
});
export type ResetProgress = z.infer<typeof ResetProgress>;

/**
 * Deletes a trial module's own data for as long as one request may run, and answers
 * with what is left. Ask again until `done`. While it isn't, the module refuses changes.
 */
export const resetModule = defineEndpoint({
  method: 'POST',
  path: '/admin/system/modules/:name/reset',
  permission: 'core.system.manage',
  recentLogin: true,
  idempotencyKey: 'required',
  params: ModuleParams,
  body: ConfirmModule,
  response: ResetProgress,
});

/** Puts a module live, for good. Already live is a no-op, answered with `changed: false`. */
export const putModuleLive = defineEndpoint({
  method: 'POST',
  path: '/admin/system/modules/:name/live',
  permission: 'core.system.manage',
  recentLogin: true,
  idempotencyKey: 'required',
  params: ModuleParams,
  body: ConfirmModule,
  response: z.object({ module: z.string(), state: ModuleState, since: timestamp.nullable(), changed: z.boolean() }),
});
